import { FhirBundleSdk } from "@metriport/fhir-sdk";
import React, { createContext, useContext } from "react";
import { AnalysisState } from "../hooks/use-resource-analysis-cache";

interface SidePanelProviderProps {
  children: React.ReactNode;
  fhirSdk: FhirBundleSdk;
  analysisState?: AnalysisState;
  onAddToExport: (docId: string, docFilename: string, pages?: number[]) => void;
  onRemoveFromExport: (docId: string) => void;
  isDocumentInExportList: (docId: string) => boolean;
  onPdfView: (docId: string, docFilename?: string, initialSearchQuery?: string) => void;
}

type Props = Exclude<SidePanelProviderProps, "children">;

const FhirSdkContext = createContext<Props["fhirSdk"]>({} as Props["fhirSdk"]); // TODO: idea to lift it up even more
const AnalysisStateContext = createContext<Props["analysisState"]>({} as Props["analysisState"]);
const DocumentCallbackContext = createContext<{
  onAddToExport: Props["onAddToExport"];
  onRemoveFromExport: Props["onRemoveFromExport"];
  isDocumentInExportList: Props["isDocumentInExportList"];
  onPdfView: Props["onPdfView"];
}>({
  onAddToExport: () => undefined,
  onRemoveFromExport: () => undefined,
  isDocumentInExportList: () => false,
  onPdfView: () => undefined,
});

export function SidePanelContextProvider({
  children,
  fhirSdk,
  analysisState,
  onAddToExport,
  onRemoveFromExport,
  isDocumentInExportList,
  onPdfView,
}: SidePanelProviderProps) {
  return (
    <FhirSdkContext.Provider value={fhirSdk}>
      <AnalysisStateContext.Provider value={analysisState}>
        <DocumentCallbackContext.Provider
          value={{ onAddToExport, onRemoveFromExport, isDocumentInExportList, onPdfView }}
        >
          {children}
        </DocumentCallbackContext.Provider>
      </AnalysisStateContext.Provider>
    </FhirSdkContext.Provider>
  );
}

export const useFhirSdkContext = () => useContext(FhirSdkContext);
export const useAnalysisStateContext = () => useContext(AnalysisStateContext);
export const useDocumentCallbackContext = () => useContext(DocumentCallbackContext);
